import { Button } from "antd";
import styled from "styled-components";

import {
  WHITE,
  GRAY_50,
  GRAY_200,
  GRAY_500,
  GRAY_700,
  PRIMARY_50,
  PRIMARY_100,
  PRIMARY_200,
  PRIMARY_600,
  PRIMARY_700,
  ERROR_50,
  ERROR_100,
  ERROR_200,
  ERROR_600,
  ERROR_700
} from "../../constant/colors";

/**
 * Shared visual styling for the Tasks hub, matching the approved mockup — a
 * neutral count chip + a lightly-dressed antd Table (muted header, thin row
 * borders, info-colored patient links, pointer rows when the row is clickable).
 */

// Neutral count chip in each section header (replaces a colored Badge) —
// `margin-left:auto` pins it to the right edge of the header.
export const CountPill = styled.span`
  margin-left: auto;
  font-size: 13px;
  line-height: 20px;
  color: ${GRAY_500};
  background: ${GRAY_50};
  border-radius: 10px;
  padding: 1px 10px;
  min-width: 26px;
  text-align: center;
`;

// Wraps an antd <Table> to restyle header + cells to the mockup. Pass
// `$clickable` for a pointer cursor on body rows (HITL sections, where the
// whole row opens the task modal).
export const TableWrap = styled.div`
  .ant-table-thead > tr > th {
    background: ${WHITE};
    color: ${GRAY_500};
    font-size: 12px;
    font-weight: 500;
    padding: 8px 16px;
    border-bottom: 1px solid ${GRAY_200};
  }
  .ant-table-tbody > tr > td {
    font-size: 13px;
    color: ${GRAY_700};
    padding: 11px 16px;
    border-bottom: 1px solid ${GRAY_200};
  }
  .ant-table-tbody > tr:last-child > td {
    border-bottom: 0;
  }
  .who-link {
    color: ${PRIMARY_600};
    font-weight: 500;
  }
  .muted {
    color: ${GRAY_500};
  }
  ${(p) => (p.$clickable ? ".ant-table-tbody > tr { cursor: pointer; }" : "")}
`;

// Soft-tinted pill for the hub's primary actions — the HITL rows' Review /
// Fill EMR data (with a leading icon; the whole row is clickable too) and the
// screenshots Submit / Confirm. Lighter + more modern than a solid primary.
export const ActionButton = styled(Button)`
  && {
    border-radius: 8px;
    font-weight: 500;
    padding: 0 14px;
    background: ${PRIMARY_50};
    border-color: ${PRIMARY_100};
    color: ${PRIMARY_700};
    box-shadow: none;
    transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
  }
  && .anticon {
    color: ${PRIMARY_600};
  }
  &&:hover,
  &&:focus {
    background: ${PRIMARY_100};
    border-color: ${PRIMARY_200};
    color: ${PRIMARY_700};
  }
  /* Used as a modal primary too (e.g. consent Send), where it can be disabled —
     the hub's row actions never are. Gray it out so a disabled action doesn't read
     as active blue. */
  &&:disabled,
  &&:disabled:hover {
    background: ${GRAY_50};
    border-color: ${GRAY_200};
    color: ${GRAY_500};
  }
`;

// Danger twin of the ActionButton pill — same soft-tinted shape in the error
// palette (the consent modal's "Don't send"). Mirrors PRIMARY_* → ERROR_* 1:1.
export const DangerActionButton = styled(Button)`
  && {
    border-radius: 8px;
    font-weight: 500;
    padding: 0 14px;
    background: ${ERROR_50};
    border-color: ${ERROR_100};
    color: ${ERROR_700};
    box-shadow: none;
    transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
  }
  && .anticon {
    color: ${ERROR_600};
  }
  &&:not(:disabled):hover,
  &&:not(:disabled):focus {
    background: ${ERROR_100};
    border-color: ${ERROR_200};
    color: ${ERROR_700};
  }
  &&:disabled,
  &&:disabled:hover {
    background: ${GRAY_50};
    border-color: ${GRAY_200};
    color: ${GRAY_500};
  }
`;

// Modern neutral secondary (the screenshots "Clear" / "Cancel") — same rounded
// shape as the ActionButton pill, in a quiet gray tone.
export const SecondaryActionButton = styled(Button)`
  && {
    border-radius: 8px;
    font-weight: 500;
    padding: 0 14px;
    box-shadow: none;
    transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
  }
  &&:not(:disabled) {
    background: ${WHITE};
    border-color: ${GRAY_200};
    color: ${GRAY_700};
  }
  &&:not(:disabled):hover,
  &&:not(:disabled):focus {
    background: ${GRAY_50};
    border-color: ${GRAY_500};
    color: ${GRAY_700};
  }
`;
